import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  /** Helper line under the label; becomes the tooltip in the segmented variant. */
  description?: React.ReactNode;
  disabled?: boolean;
  /** Segmented bars often read better as icon + text (calendar views, CAL-04). */
  icon?: LucideIcon;
}

export interface RadioGroupProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    'value' | 'onValueChange' | 'onChange' | 'children'
  > {
  value?: string;
  onChange?: (value: string) => void;
  options: RadioOption[];
  orientation?: 'horizontal' | 'vertical';
  name?: string;
  /** `segmented` renders a joined button bar instead of dots (CAL-04, CAL-07). */
  variant?: 'radio' | 'segmented';
}

/**
 * Radix resolves reading direction from a DirectionProvider only — it never
 * looks at the document — so arrow keys would walk backwards on our RTL shell.
 * NFR-03 lets a user switch language at runtime, hence the observer.
 */
function useDocumentDir(): 'rtl' | 'ltr' {
  const read = () => (typeof document !== 'undefined' && document.documentElement.dir === 'ltr' ? 'ltr' : 'rtl');
  const [dir, setDir] = React.useState<'rtl' | 'ltr'>(read);

  React.useEffect(() => {
    const root = document.documentElement;
    setDir(root.dir === 'ltr' ? 'ltr' : 'rtl');
    const observer = new MutationObserver(() => setDir(root.dir === 'ltr' ? 'ltr' : 'rtl'));
    observer.observe(root, { attributes: true, attributeFilter: ['dir'] });
    return () => observer.disconnect();
  }, []);

  return dir;
}

export const RadioGroup = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, RadioGroupProps>(
  function RadioGroup(
    { value, onChange, options, orientation = 'vertical', variant = 'radio', name, disabled, dir, className, ...props },
    ref,
  ) {
    const documentDir = useDocumentDir();
    const horizontal = orientation === 'horizontal';

    const root = (children: React.ReactNode, rootClassName: string) => (
      <RadioGroupPrimitive.Root
        ref={ref}
        name={name}
        value={value}
        onValueChange={(next) => onChange?.(next)}
        orientation={orientation}
        dir={dir ?? documentDir}
        disabled={disabled}
        className={cn(rootClassName, className)}
        {...props}
      >
        {children}
      </RadioGroupPrimitive.Root>
    );

    if (variant === 'segmented') {
      // DS-04 — the active segment is a light coloured fill with coloured text
      // and a coloured edge, the same language as an active toolbar button.
      return root(
        options.map((option, index) => {
          const Icon = option.icon;
          const isFirst = index === 0;
          const isLast = index === options.length - 1;
          return (
            <RadioGroupPrimitive.Item
              key={option.value}
              value={option.value}
              data-value={option.value}
              disabled={option.disabled}
              title={typeof option.description === 'string' ? option.description : undefined}
              className={cn(
                'inline-flex items-center justify-center gap-1.5 whitespace-nowrap bg-white px-2.5 py-1 text-xs font-bold text-slate-600 transition',
                'hover:bg-slate-50 focus:outline-none focus-visible:z-10 focus-visible:ring-1 focus-visible:ring-blue-500',
                'data-[state=checked]:bg-blue-50 data-[state=checked]:text-blue-700',
                'data-[state=checked]:ring-1 data-[state=checked]:ring-inset data-[state=checked]:ring-blue-300',
                'disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400',
                // Separators and corners are logical, so the bar joins correctly in RTL.
                horizontal ? 'border-e border-slate-300 last:border-e-0' : 'border-b border-slate-300 last:border-b-0',
                horizontal && isFirst && 'rounded-s',
                horizontal && isLast && 'rounded-e',
                !horizontal && isFirst && 'rounded-t',
                !horizontal && isLast && 'rounded-b',
              )}
            >
              {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
              <span>{option.label}</span>
            </RadioGroupPrimitive.Item>
          );
        }),
        cn(
          'rounded border border-slate-300 bg-white',
          horizontal ? 'inline-flex items-stretch' : 'inline-flex flex-col items-stretch',
        ),
      );
    }

    return root(
      options.map((option) => (
        <RadioItem key={option.value} option={option} groupDisabled={disabled} />
      )),
      horizontal ? 'flex flex-wrap items-start gap-x-4 gap-y-1.5' : 'flex flex-col gap-1.5',
    );
  },
);

function RadioItem({ option, groupDisabled }: { option: RadioOption; groupDisabled?: boolean }) {
  const id = React.useId();
  const descriptionId = `${id}-desc`;
  const isDisabled = groupDisabled || option.disabled;

  return (
    // DS-50 — flex + gap places the label on the end side without a physical margin.
    <div className="flex items-start gap-2 text-xs">
      <span className="flex h-[18px] items-center">
        <RadioGroupPrimitive.Item
          id={id}
          value={option.value}
          data-value={option.value}
          disabled={option.disabled}
          aria-describedby={option.description ? descriptionId : undefined}
          className={cn(
            'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white transition',
            'hover:border-slate-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
            'data-[state=checked]:border-blue-600',
            'disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100',
          )}
        >
          <RadioGroupPrimitive.Indicator className="block h-1.5 w-1.5 rounded-full bg-blue-600 data-[disabled]:bg-slate-400" />
        </RadioGroupPrimitive.Item>
      </span>
      <span className="min-w-0">
        <label
          htmlFor={id}
          className={cn('block leading-[18px]', isDisabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700')}
        >
          {option.label}
        </label>
        {option.description ? (
          <span id={descriptionId} className="mt-0.5 block text-[11px] leading-tight text-slate-500">
            {option.description}
          </span>
        ) : null}
      </span>
    </div>
  );
}
